import { GuestProfile } from "@closerplatform/spinner-openapi";
import classNames from "classnames";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { AudioPlaybackWithTrack } from "@fidget/common/components/call/audioPlayback";
import { CallTimeLeftTimer } from "@fidget/common/components/call/callTimeLeftTimer";
import { CallTimer } from "@fidget/common/components/call/callTimer";
import { TooltipComponent } from "@fidget/common/components/tooltipComponent";
import { L10n } from "@fidget/common/i18n";
import { IMediaTrackModel } from "@fidget/common/models/call/mediaTrack";
import { logger } from "@fidget/common/utils";
import { isScreenSharingSupported } from "@fidget/common/utils/browserSupport";
import { UserPresenter } from "@fidget/common/utils/userPresenter";
import { AgentControlCircle } from "../../components/call/agentControlCircle";
import { useStore } from "../../models";
import dashboardStyles from "../dashboard/dashboard.css";
import inboxStyles from "../inbox/inbox.css";
import styles from "./agentCallSection.css";
import { AgentLimitedCallEndedSection } from "./agentLimitedCallEndedSection";
import { AgentVideoSection } from "./agentVideoSection";
import { CallSectionButtons } from "./callSectionButtons";
import { CobrowsingButtons } from "./cobrowsingButtons";
import { useAgentServices } from "../../models/agentServices";

export const AgentCallSection = observer(() => {
  const {
    isAnyVideoEnabled,
    cobrowsingOnly,
    shouldRenderVideoSection,
    callMaxDuration,
    label,
    hideVideo,
    videoExpanded,
    guest,
    renderCobrowsing,
    hideVideoButtonClicked,
    expandVideoButtonClicked,
    onCobrowsingButtonClick,
    acceptCall,
    endCall,
    goToRoom,
    isCobrowsingSessionAccepted,
    toggleScreensharing,
    isScreensharingEnabled,
    isCobrowsingSessionInProgress,
    isCallEnded,
    isSomeoneCalling,
    clearCall,
    callStartTimestamp,
    cobrowsingStartTimestamp,
    remoteAudio,
    remoteVideo,
    cobrowsingEnabled
  } = agentCallSectionState();

  const renderScreenToggleButton = () => {
    return (
      <TooltipComponent
        text={<L10n id={"header.call.screenSharingUnavailable"} />}
        style={"dashboard"}
        tooltipShow={!isScreenSharingSupported}
        width={"240px"}
        maxWidth={"260px"}
      >
        <AgentControlCircle
          buttonStyle={{ bright: !isScreensharingEnabled, off: !isScreenSharingSupported }}
          onClick={toggleScreensharing}
        >
          <i className="icon-presentation" />
        </AgentControlCircle>
      </TooltipComponent>
    );
  };

  const renderCobrowsingButton = () => {
    return (
      <AgentControlCircle
        buttonStyle={{ bright: renderCobrowsing }}
        onClick={onCobrowsingButtonClick}
      >
        <i className="icon-cobrowsing-new" />
      </AgentControlCircle>
    );
  };

  const renderAudio = () => {
    return (
      remoteAudio &&
      remoteAudio.isPlayable &&
      remoteAudio.track && <AudioPlaybackWithTrack track={remoteAudio.track} />
    );
  };

  const renderCallingInProgress = () => {
    return (
      <div
        className={classNames(styles["call-panel"], {
          [styles["not-all-rounded-corners"]]: !hideVideo
        })}
      >
        <div className={styles["call-info"]}>
          <div className={styles.caller}>
            <button className={styles.name} disabled={guest === undefined} onClick={goToRoom}>
              {label}
            </button>
            {remoteVideo && !remoteVideo.isPlayable && (
              <i className={classNames(styles.icon, "icon-camera-crossed")} />
            )}
            {remoteAudio && !remoteAudio.isPlayable && (
              <i className={classNames(styles.icon, "icon-crossed-mic")} />
            )}
          </div>
          <div className={styles.label}>
            {isSomeoneCalling ? (
              <L10n id={"header.call.isCalling"} />
            ) : (
              <>
                <L10n id={"header.call.onCallWith"} />
                {callStartTimestamp && (
                  <>
                    <CallTimeLeftTimer
                      startTimestamp={callStartTimestamp}
                      callMaxDuration={callMaxDuration!}
                    />
                    <span className={styles["call-timer"]}>
                      <CallTimer startTimestamp={callStartTimestamp} />
                    </span>
                  </>
                )}
              </>
            )}
          </div>
        </div>
        <CallSectionButtons
          acceptCall={acceptCall}
          endCall={endCall}
          videoExpanded={videoExpanded}
          videoHidden={hideVideo}
          renderCobrowsingButton={renderCobrowsingButton}
          renderScreenToggleButton={renderScreenToggleButton}
          expandVideoButtonClicked={expandVideoButtonClicked}
          hideVideoButtonClicked={hideVideoButtonClicked}
          isAnyVideoEnabled={isAnyVideoEnabled}
          isCobrowsingEnabled={cobrowsingEnabled}
        />
      </div>
    );
  };

  const renderOnlyCobrowsingInProgress = () => {
    return (
      <div
        className={classNames(styles["call-panel"], {
          [styles["not-all-rounded-corners"]]: !hideVideo
        })}
      >
        <div className={styles["call-info"]}>
          <div className={styles.caller}>
            <button className={styles.name} disabled={guest === undefined} onClick={goToRoom}>
              {label}
            </button>
          </div>
          <div className={styles.label}>
            {isCobrowsingSessionInProgress ? (
              <>
                <L10n id={"header.cobrowsing.onCobrowsing"} />
                {cobrowsingStartTimestamp && (
                  <>
                    <CallTimeLeftTimer
                      startTimestamp={cobrowsingStartTimestamp}
                      callMaxDuration={callMaxDuration!}
                    />
                    <span className={styles["call-timer"]}>
                      <CallTimer startTimestamp={cobrowsingStartTimestamp} />
                    </span>
                  </>
                )}
              </>
            ) : (
              <L10n id={"header.cobrowsing.wantsCobrowsing"} />
            )}
          </div>
        </div>
        <CobrowsingButtons
          videoExpanded={videoExpanded}
          videoHidden={hideVideo}
          renderCobrowsingButton={renderCobrowsingButton}
          expandVideoButtonClicked={expandVideoButtonClicked}
          hideVideoButtonClicked={hideVideoButtonClicked}
          isCobrowsingSessionAccepted={isCobrowsingSessionAccepted}
        />
      </div>
    );
  };

  const renderButtons = () => {
    if (isCallEnded) {
      return (
        <AgentLimitedCallEndedSection
          label={label || ""}
          goToRoom={goToRoom}
          close={clearCall}
          callMaxDuration={callMaxDuration!}
        />
      );
    } else if (cobrowsingOnly) {
      return renderOnlyCobrowsingInProgress();
    }
    return renderCallingInProgress();
  };

  return (
    <div
      className={classNames(styles["call-section"], { [styles["video-expanded"]]: videoExpanded })}
    >
      <div className={inboxStyles.inbox} />
      <div className={classNames(styles["call-container"])}>
        {renderButtons()}
        {shouldRenderVideoSection && (
          <>
            {renderAudio()}
            <AgentVideoSection
              hideVideo={hideVideo}
              videoExpanded={videoExpanded}
              shouldRenderCobrowsing={renderCobrowsing}
              onlyCobrowsing={cobrowsingOnly}
            />
          </>
        )}
      </div>
      <div className={dashboardStyles.calendar} />
    </div>
  );
});

type Callback = () => void;

interface AgentCallSectionState {
  isAnyVideoEnabled: boolean;
  cobrowsingOnly: boolean;
  shouldRenderVideoSection: boolean;
  callMaxDuration?: number;
  label?: string;
  hideVideo: boolean;
  videoExpanded: boolean;
  guest?: GuestProfile;
  renderCobrowsing: boolean;
  isCobrowsingSessionAccepted: boolean;
  isScreensharingEnabled: boolean;
  isCobrowsingSessionInProgress: boolean;
  hideVideoButtonClicked: Callback;
  expandVideoButtonClicked: Callback;
  onCobrowsingButtonClick: Callback;
  acceptCall: Callback;
  endCall: Callback;
  goToRoom: Callback;
  toggleScreensharing: Callback;
  isCallEnded: boolean;
  isSomeoneCalling: boolean;
  remoteVideo?: IMediaTrackModel;
  remoteAudio?: IMediaTrackModel;
  clearCall: Callback;
  cobrowsingStartTimestamp?: number;
  callStartTimestamp?: number;
  cobrowsingEnabled: boolean;
}

const agentCallSectionState = (): AgentCallSectionState => {
  const { spinnerService } = useAgentServices();
  const { callStore, routerStore, currentUserStore, cobrowsingStore } = useStore();
  const [videoHiddenByAgent, setVideoHiddenByAgent] = useState<boolean>(false);
  const [videoExpanded, setVideoExpanded] = useState<boolean>(false);
  const [guest, setGuest] = useState<GuestProfile | undefined>(undefined);
  const [renderCobrowsing, setRenderCobrowsing] = useState<boolean>(false);

  const isAnyVideoEnabled =
    callStore.localCamera.isPlayable ||
    callStore.localScreen.isPlayable ||
    callStore.remoteVideo.isPlayable ||
    renderCobrowsing;
  const cobrowsingOnly = !callStore.state;
  const callMaxDuration = currentUserStore.callMaxDuration;
  const label = guest && UserPresenter.from(guest).displayedName;
  const hideVideo =
    videoHiddenByAgent ||
    !(
      callStore.remoteVideo.isPlayable ||
      callStore.localCamera.isPlayable ||
      callStore.localScreen.isPlayable ||
      renderCobrowsing
    );
  const shouldRenderVideoSection = !!(
    callStore.isInProgress || cobrowsingStore.cobrowsingSessionInProgress
  );
  const isCobrowsingSessionAccepted = !!cobrowsingStore.cobrowsingSessionInProgress;
  const isScreensharingEnabled = !!callStore.localScreen.enabled;
  const isCobrowsingSessionInProgress = !!cobrowsingStore.cobrowsingSessionInProgress;

  const isCallEnded = callStore.isEnded;
  const isSomeoneCalling = callStore.isConnecting;
  const remoteVideo = callStore.remoteVideo;
  const remoteAudio = callStore.remoteAudio;
  const clearCall = callStore.clearCall;
  const cobrowsingStartTimestamp = cobrowsingStore.startTimestamp;
  const callStartTimestamp = callStore.startTimestamp;
  const cobrowsingEnabled = !!(
    currentUserStore.orgConfig && currentUserStore.orgConfig.cobrowseEnabled
  );

  const hideVideoButtonClicked = () => {
    setVideoHiddenByAgent(!videoHiddenByAgent);
  };

  const expandVideoButtonClicked = () => {
    setVideoExpanded(!videoExpanded);
  };

  useEffect(() => {
    if (callStore.state) {
      spinnerService
        .getGuestProfile(callStore.remoteUserId!)
        .then((guestProfile) => {
          setGuest(guestProfile);
        })
        .catch((e) => {
          logger.error({ message: "Did not found guest profile on call", error: e });
          callStore.hangup();
        });
    } else if (cobrowsingStore.cobrowsingSessionId) {
      spinnerService
        .getGuestProfileByRoom(cobrowsingStore.cobrowsingSessionId)
        .then((guestProfile) => {
          setGuest(guestProfile);
        })
        .catch((e) => {
          logger.error({ message: "Did not found guest profile on cobrowsing", error: e });
          cobrowsingStore.endCobrowsingSession();
        });
    }
  }, []);

  useEffect(() => {
    if (!!cobrowsingStore.cobrowsingSessionInProgress) {
      cobrowsingStore.indicateSessionStart();
    }
    setRenderCobrowsing(!!cobrowsingStore.cobrowsingSessionInProgress);
  }, [cobrowsingStore.cobrowsingSessionInProgress]);

  const sendCobrowsingProposal = () => {
    if (guest) {
      currentUserStore.sendCobrowsingProposalToRoom(guest.roomId);
    }
  };

  const onCobrowsingButtonClick = () => {
    if (renderCobrowsing && guest) {
      cobrowsingStore.sendCobrowsingEndedMessage(guest.roomId); // in that state it is defined
    } else if (!cobrowsingStore.cobrowsingSessionAccepted) {
      sendCobrowsingProposal();
      cobrowsingStore.acceptCobrowsingSession();
    } else {
      cobrowsingStore.acceptCobrowsingSession();
    }
  };

  const goToRoom = () => {
    return guest && routerStore.goToRoom(guest.roomId);
  };

  const acceptCall = () => {
    callStore.answer();
    goToRoom();
  };

  const endCall = () => {
    callStore.hangup();
  };

  const toggleScreensharing = () => isScreenSharingSupported && callStore.toggleScreen();

  return {
    isAnyVideoEnabled,
    cobrowsingOnly,
    shouldRenderVideoSection,
    callMaxDuration,
    label,
    hideVideo,
    videoExpanded,
    guest,
    renderCobrowsing,
    hideVideoButtonClicked,
    expandVideoButtonClicked,
    onCobrowsingButtonClick,
    acceptCall,
    endCall,
    goToRoom,
    isCobrowsingSessionAccepted,
    toggleScreensharing,
    isScreensharingEnabled,
    isCobrowsingSessionInProgress,
    isCallEnded,
    isSomeoneCalling,
    clearCall,
    callStartTimestamp,
    cobrowsingStartTimestamp,
    remoteAudio,
    remoteVideo,
    cobrowsingEnabled
  };
};
